/**
 * Remaining contracts: time entries, attachments, notifications, push,
 * automations, saved views, templates, search, reports, import/export.
 * Spec §6.9–§6.19, §7.5, §7.7.
 */
import { z } from 'zod';
import { APPROVAL_STATUSES, ATTACHMENT_ENTITY_TYPES, AUTOMATION_ACTIONS, AUTOMATION_OPERATORS, AUTOMATION_TRIGGERS, MAX_AUTOMATION_CONDITIONS, NOTIFICATION_TYPES, SAVED_VIEW_SCOPES, TEMPLATE_KINDS, VIEW_TYPES, } from './enums.js';
import { objectIdSchema } from './common.js';
/* ── Time tracking (§6.10, F9) ─────────────────────────── */
export const startTimerSchema = z
    .object({ taskId: objectIdSchema, description: z.string().max(300).optional() })
    .strict();
export const stopTimerSchema = z
    .object({ description: z.string().max(300).optional() })
    .strict()
    .optional();
export const createTimeEntrySchema = z
    .object({
    taskId: objectIdSchema,
    startedAt: z.coerce.date(),
    durationMinutes: z.number().int().min(1).max(24 * 60),
    description: z.string().max(300).optional(),
    billable: z.boolean().default(false),
})
    .strict();
export const updateTimeEntrySchema = z
    .object({
    startedAt: z.coerce.date(),
    durationMinutes: z.number().int().min(1).max(24 * 60),
    description: z.string().max(300).nullish(),
    billable: z.boolean(),
})
    .strict()
    .partial();
export const listTimeEntriesQuerySchema = z
    .object({
    userId: objectIdSchema.optional(),
    taskId: objectIdSchema.optional(),
    projectId: objectIdSchema.optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    approvalStatus: z.enum(APPROVAL_STATUSES).optional(),
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(50),
})
    .strict();
export const timesheetQuerySchema = z
    .object({
    userId: objectIdSchema.optional(),
    from: z.coerce.date(),
    to: z.coerce.date(),
})
    .strict();
export const timesheetSubmitSchema = z
    .object({ from: z.coerce.date(), to: z.coerce.date() })
    .strict();
export const timeRejectSchema = z.object({ reason: z.string().min(1).max(300) }).strict();
export const TIME_ENTRY_EDIT_WINDOW_DAYS = 7;
export const IDLE_TIMER_HOURS = 8;
/* ── Attachments (§6.9, F8) ────────────────────────────── */
export const ALLOWED_UPLOAD_EXTENSIONS = [
    'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'heic',
    'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
    'csv', 'txt', 'md', 'zip', 'mp4', 'mov', 'log', 'json', 'apk', 'ipa',
];
export const MAX_UPLOAD_BATCH = 10;
export const presignSchema = z
    .object({
    fileName: z.string().min(1).max(255),
    mimeType: z.string().min(1).max(160),
    sizeBytes: z.number().int().min(1),
    entityType: z.enum(ATTACHMENT_ENTITY_TYPES),
    entityId: objectIdSchema,
})
    .strict();
export const completeUploadSchema = z
    .object({ checksum: z.string().max(128).optional() })
    .strict()
    .optional();
/* ── Notifications (§6.14, F14) ────────────────────────── */
export const listNotificationsQuerySchema = z
    .object({
    unread: z.union([z.boolean(), z.string()]).transform((v) => v === true || v === 'true').optional(),
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(30),
})
    .strict();
export const snoozeNotificationSchema = z
    .object({ until: z.coerce.date() })
    .strict();
export const pushSubscriptionSchema = z
    .object({
    endpoint: z.string().url().max(600),
    keys: z.object({ p256dh: z.string().min(1).max(200), auth: z.string().min(1).max(200) }),
})
    .strict();
export const pushUnsubscribeSchema = z.object({ endpoint: z.string().url().max(600) }).strict();
/**
 * Events that email by default (§F14, extended by V5-05 §2.3).
 *
 * The rule is "someone else did something that needs you to know outside the
 * app". Everything here is either directed at you personally or is a decision
 * you asked for. Broadcast noise — sprint start/close, automation fired — stays
 * in-app, because an inbox that cries wolf gets filtered.
 *
 * Turning one of these on by default is not the same as forcing it: every
 * entry remains switchable per user in Settings → Notifications.
 */
export const EMAIL_BY_DEFAULT = [
    // Work assigned to you, or waiting on you.
    'task.assigned',
    'task.mentioned',
    'task.review_requested',
    'task.overdue',
    'task.due_soon',
    // Someone replied on work that is yours.
    'task.comment',
    // Work you raised or handed over reached its end state.
    'task.status_changed',
    // You now have access to something you did not have before.
    'project.added',
    // A message you did not read while you were away — the whole point of the
    // catch-up email is that it reaches you when the app cannot.
    'chat.message',
    // Leave: the approver needs the request, the requester needs the answer.
    'leave.requested',
    'leave.approved',
    'leave.rejected',
    // Digests are opt-out summaries that exist to be emailed.
    'digest.founder',
    'digest.member',
    'digest.lead_weekly',
];
/** Defaults for a brand-new user's matrix (§F14). */
export const DEFAULT_NOTIFICATION_MATRIX = Object.fromEntries(NOTIFICATION_TYPES.map((t) => [
    t,
    {
        inApp: true,
        email: EMAIL_BY_DEFAULT.includes(t),
        whatsapp: false,
        push: false,
    },
]));
/**
 * Turned on the first time a browser is registered for push — V7-01 §2.
 *
 * Without this, enrollment enrols people into nothing. Every push row in
 * `DEFAULT_NOTIFICATION_MATRIX` is `false`, so a person who accepted the
 * banner, granted permission and registered a device would still receive
 * exactly zero notifications — which is the founder's original complaint
 * restated, one layer down.
 *
 * The same shape as `WHATSAPP_OPT_IN_DEFAULTS` and for the same reason:
 * opting into a channel means opting into the events that channel is *for*.
 * Kept deliberately short — push interrupts a person who is not looking at the
 * app, so it is the four events where somebody is actually waiting on them,
 * not everything that could conceivably be sent. Applied once, on the first
 * device only, so it can never re-enable a row someone has turned off.
 */
export const PUSH_OPT_IN_DEFAULTS = [
    'task.assigned',
    'task.mentioned',
    'task.comment',
    'chat.message',
];
/** Turned on the moment a user opts into WhatsApp with a verified phone (D7). */
export const WHATSAPP_OPT_IN_DEFAULTS = [
    'task.assigned',
    'task.overdue',
    'digest.founder',
    'digest.member',
    'digest.lead_weekly',
];
export const NOTIFICATION_BATCH_WINDOW_MINUTES = 5;
export const NOTIFICATION_BATCH_THRESHOLD = 3;
/* ── Automations (§6.16, F16) ──────────────────────────── */
export const automationConditionSchema = z.object({
    field: z.string().min(1).max(60),
    operator: z.enum(AUTOMATION_OPERATORS),
    value: z.any().optional(),
    joiner: z.enum(['AND', 'OR']).default('AND'),
});
export const automationActionSchema = z.object({
    type: z.enum(AUTOMATION_ACTIONS),
    config: z.record(z.any()).default({}),
});
export const createAutomationSchema = z
    .object({
    name: z.string().min(1).max(120),
    description: z.string().max(500).optional(),
    projectId: objectIdSchema.nullish(),
    isEnabled: z.boolean().default(false),
    trigger: z.object({
        type: z.enum(AUTOMATION_TRIGGERS),
        config: z.record(z.any()).default({}),
    }),
    conditions: z.array(automationConditionSchema).max(MAX_AUTOMATION_CONDITIONS).default([]),
    actions: z.array(automationActionSchema).min(1).max(10),
})
    .strict();
export const updateAutomationSchema = createAutomationSchema.partial().strict();
export const testAutomationSchema = z.object({ taskId: objectIdSchema }).strict();
/* ── Saved views (§6.18, F10) ──────────────────────────── */
export const createSavedViewSchema = z
    .object({
    name: z.string().min(1).max(80),
    icon: z.string().max(8).optional(),
    scope: z.enum(SAVED_VIEW_SCOPES).default('private'),
    projectId: objectIdSchema.nullish(),
    viewType: z.enum(VIEW_TYPES),
    filters: z.record(z.any()).default({}),
    groupBy: z.string().max(40).nullish(),
    sortBy: z.string().max(40).nullish(),
    sortDir: z.enum(['asc', 'desc']).default('desc'),
    visibleColumns: z.array(z.string().max(40)).max(30).default([]),
    isPinned: z.boolean().default(false),
    order: z.number().int().min(0).default(0),
})
    .strict();
export const updateSavedViewSchema = createSavedViewSchema.partial().strict();
/* ── Templates (§6.19, F18) ────────────────────────────── */
export const createTemplateSchema = z
    .object({
    kind: z.enum(TEMPLATE_KINDS),
    name: z.string().min(1).max(120),
    description: z.string().max(500).optional(),
    projectId: objectIdSchema.nullish(),
    payload: z.record(z.any()),
})
    .strict();
export const updateTemplateSchema = createTemplateSchema.partial().strict();
export const applyTaskTemplateSchema = z
    .object({
    projectId: objectIdSchema,
    overrides: z.record(z.any()).default({}),
})
    .strict();
/* ── Search (§F12) ─────────────────────────────────────── */
export const SEARCH_TYPES = ['task', 'note', 'project', 'user', 'comment'];
export const searchQuerySchema = z
    .object({
    q: z.string().trim().min(1).max(200),
    types: z
        .union([z.string(), z.array(z.string())])
        .transform((v) => (Array.isArray(v) ? v : v.split(',')))
        .pipe(z.array(z.enum(SEARCH_TYPES)))
        .optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
})
    .strict();
/* ── Reports (§F13) ────────────────────────────────────── */
export const reportRangeQuerySchema = z
    .object({
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    projectId: objectIdSchema.optional(),
    teamId: objectIdSchema.optional(),
    userId: objectIdSchema.optional(),
    groupBy: z.string().max(30).optional(),
})
    .strict();
export const workloadQuerySchema = z
    .object({
    from: z.coerce.date(),
    to: z.coerce.date(),
    teamId: objectIdSchema.optional(),
    projectId: objectIdSchema.optional(),
})
    .strict();
export const auditQuerySchema = z
    .object({
    actorId: objectIdSchema.optional(),
    action: z.string().max(60).optional(),
    entityType: z.string().max(40).optional(),
    entityId: objectIdSchema.optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(50),
})
    .strict();
/* ── Import / export (§F20) ────────────────────────────── */
export const importTasksQuerySchema = z
    .object({
    projectId: objectIdSchema,
    dryRun: z.union([z.boolean(), z.string()]).transform((v) => v === true || v === 'true').default(false),
    mapping: z.string().max(4000).optional(),
})
    .strict();
export const exportWorkspaceSchema = z
    .object({ includeOwnPersonalNotes: z.boolean().default(false) })
    .strict();
//# sourceMappingURL=misc.js.map